import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { BookOpen, Heart, LogOut, Moon, Settings, Sun } from "lucide-react";
import { NAV_ITEMS } from "@/config/navigation";
import appIcon from "@/assets/app-icon.png";
import { AcaoBarraLateral, BarraLateral } from "./BarraLateral";
import type { ItemBarraLateral } from "./BarraLateral";

/**
 * A barra lateral do app no computador: as quatro abas da navbar, mais a
 * biblioteca e as salvas, que no celular vivem dentro de "Mais". Montada uma
 * vez, no Chrome. Plano de refeições fica de fora, por decisão de produto.
 */
const DesktopSidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const p = location.pathname;

  const itens: ItemBarraLateral[] = [
    ...NAV_ITEMS.map((item) => ({
      key: item.key,
      icon: item.icon,
      path: item.path,
      label: item.i18nKey ? t(item.i18nKey, item.label) : item.label,
    })),
    { key: "my-content", icon: BookOpen, path: "/my-content", label: t("home.your_contents", "My Content") },
    { key: "favorites", icon: Heart, path: "/saved-recipes", label: t("home.your_favorites", "Favorites") },
  ];

  const isActive = (path: string) => {
    if (path === "/home") return p === "/" || p === "/home";
    return p === path || p.startsWith(path + "/");
  };
  // O mais específico ganha: /my-content e /saved-recipes antes de /more.
  const ativo =
    itens.filter((i) => i.key !== "more").find((i) => isActive(i.path))?.key ??
    (isActive("/more") || ["/profile", "/settings", "/support", "/help", "/shopping-list"].some((r) => p.startsWith(r)) ? "more" : null);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <BarraLateral
      marca={{ icone: appIcon, nome: "Glucofy" }}
      itens={itens}
      ativo={ativo}
      aoEscolher={(item) => navigate(item.path)}
      rodape={
        <>
          <AcaoBarraLateral
            icone={theme === "dark" ? Moon : Sun}
            rotulo={theme === "dark" ? t("more.dark_mode", "Dark mode") : t("more.light_mode", "Light mode")}
            onClick={toggleTheme}
          />
          <AcaoBarraLateral icone={Settings} rotulo={t("more.settings", "Settings")} onClick={() => navigate("/settings")} />
          <AcaoBarraLateral icone={LogOut} rotulo={t("profile.logout", "Sign out")} onClick={handleLogout} cor="hsl(var(--sem-rojo))" />
        </>
      }
    />
  );
};

export default DesktopSidebar;
